import type { AggregateResult } from 'src/components/followUp/common/types';

/**
 * Checks if the given date parts start with numbers or strings.
 * This helps determine if a date is in one of these formats:
 * - 30.01.2025 -> first and second date part will be numeric
 * - 30. January -> second date part will not be numeric
 * - January 2025 -> first date part will not be numeric
 */
const checkIsNumericDate = (dateParts: string[]) =>
  !Number.isNaN(dateParts[0]) &&
  !Number.isNaN(parseInt(dateParts[0])) &&
  !Number.isNaN(dateParts[1]) &&
  !Number.isNaN(parseInt(dateParts[1]));

/**
 * Chart-js accepts tuples for rendering labels with multiple lines.
 * The labels for this chart are designed to be dates in various formats. This
 * helper function handles the various rules for line breaks for each format.
 */
export const getLabelList = (results: AggregateResult[]) =>
  results.map((result) => {
    const datePartsByDot = result.label.split('.');
    const isNumericDate = checkIsNumericDate(datePartsByDot);

    if (isNumericDate && datePartsByDot.length === 3) {
      // Assume a date in the format dd.mm.yyyy
      return [`${datePartsByDot[0]}.${datePartsByDot[1]}.`, datePartsByDot[2]];
    }

    const datePartsByDash = result.label.split('-');
    if (isNumericDate && datePartsByDot.length > 3) {
      // More sdate parts most likely means multiple dates divided by dash
      if (datePartsByDash.length === 2) {
        return [`${datePartsByDash[0]} -`, datePartsByDash[1]];
      }
    }

    if (!isNumericDate) {
      const datePartsBySpace = result.label.split(' ');
      if (datePartsByDash.length > 1 && datePartsBySpace.length > 2) {
        // None numeric date with a dash and multiple dots point to a format `Week 20 12. - 18.05.2025`
        return [
          `${datePartsBySpace[0]} ${datePartsBySpace[1]}`,
          datePartsBySpace.splice(2).join(' '),
        ];
      }
      // Else the date format is most likely either `30. January` or `January 2025`
      return datePartsBySpace;
    }
    return result.label;
  });

export const getValueList = (results: AggregateResult[]) =>
  results.map(({ value }) => value ?? NaN);

const mergeLabel = (label: string | string[]) => {
  if (!Array.isArray(label)) return label;

  return label.join(label[0].endsWith('.') ? '' : ' ');
};

export const condenseGaps = (
  data: number[],
  labels: Array<string | string[]>,
) => {
  const newData: number[] = [];
  const newLabels: Array<string | string[]> = [];
  let gapStartIndex: null | number = null;
  let dataContainsValues = false;

  const startNewGap = (i: number) => {
    newData.push(NaN);
    newLabels.push(labels[i]);
    gapStartIndex = i;
  };

  const endCurrentGap = (i: number, gapStartIndex: number) => {
    const gapLabelStartDate = mergeLabel(labels[gapStartIndex]);
    const gapLabelEndDate = mergeLabel(labels[i]);

    newLabels.fill(
      [`${gapLabelStartDate} -`, gapLabelEndDate],
      newLabels.length - 1,
      newLabels.length,
    );
  };

  const addValidData = (i: number) => {
    newData.push(data[i]);
    newLabels.push(labels[i]);
    gapStartIndex = null;
    dataContainsValues = true;
  };

  for (let i = 0; i < data.length; i++) {
    if (isNaN(data[i])) {
      if (gapStartIndex === null && dataContainsValues) {
        // New gap - validValueDetected to avoid gaps at the start
        if (data[i + 1] !== undefined) {
          startNewGap(i);
        }
      } else if (
        gapStartIndex !== null &&
        data[i + 1] !== undefined &&
        !isNaN(data[i + 1])
      ) {
        // Close the gap if the next data point is a valid value
        endCurrentGap(i, gapStartIndex);
      } else if (data[i + 1] === undefined) {
        // Remove gap at the end
        newLabels.pop();
        newData.pop();
      }
    } else {
      addValidData(i);
    }
  }

  return { data: newData, labels: newLabels };
};
